import { useState, useEffect, useCallback, useRef } from 'react';
import { getWebSocketUrl } from '@/utils/helpers';

/**
 * Custom hook for real-time score updates via WebSocket
 * @param {Function} onScoreUpdate - Callback when a score update is received
 * @returns {Object} - { isConnected, lastUpdate }
 */
export const useScoreWebSocket = (onScoreUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  // Stash onScoreUpdate in a ref so `connect` doesn't depend on it. Callers usually
  // pass an inline function which changes every render — without this, the hook
  // would tear down and re-open the WebSocket on every parent re-render.
  const onScoreUpdateRef = useRef(onScoreUpdate);
  useEffect(() => { onScoreUpdateRef.current = onScoreUpdate; }, [onScoreUpdate]);

  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.warn('WebSocket URL not configured');
      return;
    }

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected for live scores');
        setIsConnected(true);
        
        // Send ping every 30 seconds to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          // Handle pong response
          if (event.data === 'pong') return;
          
          const message = JSON.parse(event.data);
          if (message.type === 'score_update' && message.data) {
            console.log('Score update received:', message.data);
            setLastUpdate(new Date());
            const cb = onScoreUpdateRef.current;
            if (cb) cb(message.data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect WebSocket...');
          connect();
        }, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }, []);   // Stable — reads the latest callback via the ref

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { isConnected, lastUpdate };
};

export default useScoreWebSocket;
